<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6">

    <!-- Return Form Card -->
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] print-hide">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Return To Stock (RTS)</h2>
        <span v-if="loading" class="text-xs text-slate-400 animate-pulse text-right">Loading data...</span>
      </div>
      <form @submit.prevent="processReturn" class="px-6 pb-4 pt-2">
        <div class="flex flex-col gap-y-2 text-left">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2">
            <div class="flex flex-col gap-1">
              <label class="text-[0.78rem] font-semibold text-[#1e293b]">RT Number</label>
              <input
                :value="rtNumberDisplay || '-'"
                type="text"
                readonly
                class="w-full px-2.5 py-1.5 border border-[#e2e8f0] rounded-md outline-none text-sm bg-slate-50 text-slate-600 font-semibold cursor-not-allowed"
              />
            </div>
            <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Transaction Date</label>
            <div class="relative flex items-center">
              <input
                :value="formatDateForDisplay(form.transactionDate)"
                type="text"
                class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none text-sm bg-white"
                readonly
              />
              <input
                v-model="form.transactionDate"
                type="date"
                :min="todayDate"
                :max="todayDate"
                class="absolute inset-0 w-full h-full opacity-1 bg-transparent border-none outline-none text-transparent cursor-pointer px-2.5 py-1.5 text-sm"
                style="color: transparent;"
                required
              />
            </div>
            </div>

            <!-- Department (from token) -->
            <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">From</label>
            <select
              v-model="form.requestedDepartment"
              @change="handleDeptChange"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
              required
            >
              <option value="" disabled>Select Department</option>
              <option v-for="dept in userDeptList" :key="dept" :value="dept">{{ dept }}</option>
            </select>
            </div>

            <!-- Category -->
            <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">To</label>
            <select v-model="form.categoryId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null" disabled>Select Category</option>
              <option v-for="cat in selectableCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-2">
            <!-- Item Name -->
            <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Item Name</label>
            <select v-model="form.itemId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" :disabled="!form.categoryId" required>
              <option :value="null" disabled>Select Item</option>
              <option v-for="item in filteredItems" :key="item.id" :value="item.id">{{ item.itemName }} ({{ item.unit }})</option>
            </select>
            </div>

            <!-- Quantity -->
            <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Quantity</label>
            <input  v-model="form.quantity" type="number" min="0"    step="any"
            inputmode="decimal" placeholder="0"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
            </div>

            <!-- Volume -->
            <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">
              Volume
              <span class="text-red-600" v-if="selectedItem?.unitProduction">({{ selectedItem.unitProduction }})</span>
            </label>
            <input v-model.number="form.volume" type="number" min="0" step="0.01" 
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm" required>
            </div>

            <!-- Expiry Date -->
            <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Expiry Date</label>
            <input
              v-model="form.expiryDate"
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
            </div>

          <!-- Unit Price (Hidden, auto-filled) -->
          <div v-if="false" class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Unit Price</label>
            <input v-model.number="form.unitPrice" type="number" min="0" step="0.01" placeholder="0.00"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm">
          </div>

          <!-- Total Cost (Hidden, auto-filled) -->
          <div v-if="false" class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Total Price</label>
            <input :value="totalCost.toFixed(2)" type="text" readonly
              class="px-2.5 py-1.5 border border-[#e2e8f0] rounded-md bg-slate-50 text-slate-600 font-semibold text-sm cursor-not-allowed">
          </div>

            <!-- Reason -->
            <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Reason</label>
            <input v-model="form.remark" type="text" placeholder="e.g. Wrong size, damaged..."
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm">
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-3 pt-3 border-t border-slate-100 flex justify-end gap-3">
          <button v-if="isEditing" type="button" @click="cancelEdit"
            class="px-5 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md hover:bg-slate-300 transition-all">Cancel</button>
          <button type="submit"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95">
            {{ isEditing ? 'Update Item' : 'Add to List' }}
          </button>
        </div>
      </form>
    </div>



    <!-- Table Section (Current Draft) -->
    <div v-if="returnList.length > 0" class="flex flex-col gap-4">

      <div class="w-full overflow-x-auto shadow-sm rounded-xl print-container">
        <!-- Print Header (Hidden unless printing) -->
        <div class="hidden print:block mb-6">
          <div class="print-doc-header print-doc-header-grid">
            <div class="print-doc-left">
              <div class="print-company-block">
                <img
                  v-if="printCompanyLogo"
                  :src="printCompanyLogo"
                  :alt="printCompanyName || 'Company Logo'"
                  class="print-company-logo"
                />
                <div v-else class="print-company-logo-fallback">Logo</div>
              </div>
            </div>
            <div class="print-doc-title-wrap">
              <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
              <h1 class="print-doc-title text-xl font-bold text-center mb-2">Return To Stock (RTS)</h1>
            </div>
            <div class="print-doc-meta print-doc-right">
              <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
            </div>
          </div>
          <hr class="my-4 border-black" />
        </div>

        <table class="w-full border-collapse bg-white overflow-hidden print-table">
          <thead>
            <tr>
              <th>#</th>
              <th>R No</th>
              <th>Department</th>
              <th>Category</th>
              <th>Item</th>
              <th>Qty</th>
              <th>Volume</th>
              <th>Expiry Date</th>
              <!-- <th>Unit Price</th> -->
              <!-- <th>Total Cost</th> -->
              <th>Reason</th>
              <th class="print-hide">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(ret, index) in returnList" :key="index" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <td>{{ ret.returnNo }}</td>
              <td>{{ ret.requestedDepartment }}</td>
              <td>{{ getCategoryName(ret.categoryId) }}</td>
              <td>{{ getItemName(ret.itemId) }}</td>
              <td>{{ ret.quantity }}</td>
              <td>{{ ret.volume ?? '-' }}</td>
              <td>{{ ret.expiryDate || '-' }}</td>
              <!-- <td>{{ ret.unitPrice.toFixed(2) }}</td> -->
              <!-- <td>{{ (ret.totalPrice || 0).toLocaleString() }}</td> -->
              <td>{{ ret.remark || '-' }}</td>
              <td class="print-hide">
                <div class="flex items-center gap-1">
                  <button @click="editReturn(index)" class=" editbtn hover:underline text-emerald-600">Edit</button>
                  <button @click="deleteReturn(index)" class="delbtn p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">Delete</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Print Footer (Hidden unless printing) -->
        <div class="hidden print:block mt-8 w-full">
          <div class="flex flex-col gap-1.5 pl-8">
            <div class="flex items-baseline gap-2 text-sm border-t border-black pt-1 mt-1">
              <span class="font-black text-[#0d4169]">Total Amount:</span>
              <span class="font-black text-base text-[#0d4169]">{{ grandTotal.toFixed(2) }}</span>
            </div>
          </div>

          <div class="mt-16 flex justify-between px-10 text-sm">
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Prepared By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Checked By</strong>
            </div>
            <div class="text-center">
              <div class="border-b border-black w-40 mb-2"></div>
              <strong>Authorized By</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Total Amount + Save Button (below table) -->
      <div class="flex justify-between items-center px-2 pt-2 print-hide">
        <div class="text-left">
          <p class="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Amount</p>
          <p class="text-sm font-black text-[#0d4169]">{{ grandTotal.toFixed(2) }}</p>
        </div>
        <button @click="saveToDatabase" :disabled="isSaving"
          class="min-w-[120px] px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50 relative flex items-center justify-center">
          <div
  v-if="isSaving"
  class="absolute h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
></div>
        <span :class="isSaving ? 'invisible' : 'visible'">{{'Save Return' }}</span>  
        </button>
      </div>

    </div>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

const categories = ref([])
const registeredItems = ref([])
const departments = ref([])

const loading = ref(false)
const isSaving = ref(false)
const isEditing = ref(false)
const editIndex = ref(-1)
const pendingReturns = ref([])
// const pendingSortKey = ref('department')
// const pendingSortOrder = ref('asc')

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

function parseAssignedCategoryIds(raw) {
  if (!raw) return []
  return String(raw)
    .split(',')
    .map(v => Number(String(v).trim()))
    .filter(v => Number.isInteger(v) && v > 0)
}

function filterCategoriesByUserAssignment(list) {
  const cats = Array.isArray(list) ? list : []
  const user = currentUser.value
  let privilege = {}
  const rawPriv = user?.privilege
  if (rawPriv) {
    try {
      privilege = typeof rawPriv === 'string' ? JSON.parse(rawPriv) : rawPriv
    } catch {
      privilege = {}
    }
  }
  const hasAll = privilege?.All === true || privilege?.all === true
  const assignedIds = parseAssignedCategoryIds(user?.category_ids || user?.categoryIds)

  if (hasAll && assignedIds.length === 0) {
    return cats
  }
  if (assignedIds.length === 0) {
    return cats
  }
  const idSet = new Set(assignedIds)
  return cats.filter(c => idSet.has(Number(c.id)))
}

// Select Category options: All:true + empty category_ids → all; otherwise assigned category_ids.
const selectableCategories = computed(() => filterCategoriesByUserAssignment(categories.value))

// Display-only RT number fetched from backend.
// This is intentionally NOT sent in the save/update payload.
const rtNumberDisplay = ref('')

// Department list from token (comma-separated string)
const userDeptList = computed(() => {
  // Parse privilege — may be a JSON string or an object
  const rawPriv = currentUser.value?.privilege
  let privilege = {}
  if (rawPriv) {
    try {
      privilege = typeof rawPriv === 'string' ? JSON.parse(rawPriv) : rawPriv
    } catch { privilege = {} }
  }
  const hasAll = privilege?.all === true || privilege?.All === true

  // If user has All privilege, show every fetched department
  if (hasAll) {
    return departments.value.map(d => d.name).filter(Boolean)
  }

  // Otherwise show only the departments assigned to this user
  if (!currentUser.value?.departments) return []
  return currentUser.value.departments.split(',').map(d => d.trim()).filter(Boolean)
})

const getInitialForm = () => ({
  returnNo: '',
  transactionDate: new Date().toISOString().slice(0, 10),
  requestedDepartment: '',
  departmentId: null,
  categoryId: null,
  itemId: null,
  quantity: null,
  volume: "",
  expiryDate: '',
  unitPrice: null,
  remark: '',
})
const form = ref(getInitialForm())
const todayDate = new Date().toISOString().slice(0, 10)
function formatDateForDisplay(isoDate) {
  const s = String(isoDate || '')
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return s
  return `${m[3]}/${m[2]}/${m[1]}`
}


const totalCost = computed(() => Number(form.value.quantity || 0) * Number(form.value.unitPrice || 0))

const filteredItems = computed(() => {
  if (!form.value.categoryId) return []
  return registeredItems.value
    .filter(item => item.categoryId === form.value.categoryId)
    .slice()
    .sort((a, b) => String(a?.itemName || '').localeCompare(String(b?.itemName || ''), undefined, { sensitivity: 'base' }))
})

const selectedItem = computed(() => {
  if (!form.value.itemId) return null
  return registeredItems.value.find(i => i.id == form.value.itemId) || null
})
 
function normalizeUnit(unit) {
  return String(unit || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
}

function isLiterOrKgUnit(unit) {
  const normalizedUnit = normalizeUnit(unit)
  return normalizedUnit === 'liter' || normalizedUnit === 'k.g.' || normalizedUnit === 'kg'
}

function computeUnitPrice(item) {
  const recentUnitPrice = item?.recent_unit_price !== undefined && item?.recent_unit_price !== null
    ? Number(item.recent_unit_price)
    : 0
  const recentVolume = item?.recent_volume !== undefined && item?.recent_volume !== null
    ? Number(item.recent_volume)
    : 0

  if (!(recentVolume > 0)) return 0

  const isLiterOrKg = isLiterOrKgUnit(item?.unit)
  return isLiterOrKg
    ? (recentUnitPrice / recentVolume) * 1000
    : (recentUnitPrice / recentVolume)
}

// Watch itemId to auto-fill unitPrice from items.recent_unit_price with unit conversion rules
watch(() => form.value.itemId, (newId) => {
  if (!newId) {
    form.value.unitPrice = null
    return
  }
  // Use loose equality (==) to handle potential type mismatch (string from select vs number from API)
  const item = registeredItems.value.find(i => i.id == newId)
  if (item) {
    form.value.unitPrice = computeUnitPrice(item)
  } else {
    console.warn(`Item with ID ${newId} not found in registeredItems`)
    form.value.unitPrice = null
  }
})

const returnList = ref([])
const grandTotal = computed(() => returnList.value.reduce((sum, item) => sum + (item.totalPrice || 0), 0))

// Resolve department name → departmentId from the loaded list
function resolveDepartmentId(name) {
  const matched = departments.value.find(d => d.name === name)
  return matched ? matched.id : null
}

function handleDeptChange() {
  form.value.departmentId = resolveDepartmentId(form.value.requestedDepartment)
}

async function loadData() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const [catRes, itemRes, depRes] = await Promise.all([
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`),
      apiFetch(`/departments?${query}`)
    ])
    if (catRes.ok) categories.value = await catRes.json()
    categories.value = categories.value.filter(c => c.status === 1);
  categories.value.sort((a, b) => a.name.localeCompare(b.name))
    if (itemRes.ok) {
      const items = await itemRes.json()
      registeredItems.value = (Array.isArray(items) ? items : []).map((item) => ({
        ...item,
        // Keep pricing snapshot fields explicit for return unit-price calculation.
        recent_unit_price: item?.recent_unit_price ?? null,
        recent_volume: item?.recent_volume ?? null,
      }))
    }
    if (depRes.ok) departments.value = await depRes.json()
    departments.value = departments.value.filter(d => d.status === 1);
  departments.value.sort((a, b) => a.name.localeCompare(b.name))
    await fetchPendingReturns()
  } catch (err) { console.error(err) }
  finally { loading.value = false }
}

async function fetchPendingReturns() {
  if (!companyId.value && !branchId.value) return
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/return?${query}&isApproved=0`)
    if (res.ok) {
      pendingReturns.value = await res.json()
    }
  } catch (err) { console.error(err) }
}


function processReturn() {
  if (form.value.unitPrice === null || form.value.unitPrice === undefined) {
    alert('Could not determine unit price for the selected item.')
    return
  }
  const record = {
    ...form.value,
    totalPrice: totalCost.value,
  }

  if (isEditing.value) {
    returnList.value[editIndex.value] = record
    isEditing.value = false
    editIndex.value = -1
  } else {
    returnList.value.unshift(record)
  }
  localSave()

  const savedHeader = {
    transactionDate: form.value.transactionDate,
    requestedDepartment: form.value.requestedDepartment,
    departmentId: form.value.departmentId,
  }
  form.value = { ...getInitialForm(), ...savedHeader }
}

function editReturn(index) {
  form.value = { ...returnList.value[index] }
  isEditing.value = true
  editIndex.value = index
}

function cancelEdit() {
  isEditing.value = false
  editIndex.value = -1
  const savedHeader = {
    transactionDate: form.value.transactionDate,
    requestedDepartment: form.value.requestedDepartment,
    departmentId: form.value.departmentId,
  }
  form.value = { ...getInitialForm(), ...savedHeader }
}

function deleteReturn(index) {
  returnList.value.splice(index, 1)
  localSave()
}

async function saveToDatabase() {
  if (!returnList.value.length) return
  isSaving.value = true
  try {
    const transformedItems = returnList.value.map(i => {
      const itemMeta = registeredItems.value.find(r => r.id == i.itemId)
      const isLiterOrKg = isLiterOrKgUnit(itemMeta?.unit)
      const rawQuantity = Number(i.quantity || 0)
      const rawUnitPrice = Number(i.unitPrice || 0)
      const volume = Number(i.volume || 0)

      const quantity = rawQuantity
      const unitPrice = isLiterOrKg
        ? (rawUnitPrice * volume) / 1000
        : rawUnitPrice
      const totalPrice = quantity * unitPrice

      return {
        itemId: i.itemId,
        quantity,
        volume: i.volume,
        expiryDate: i.expiryDate || null,
        variantDetails: i.volume != null ? String(i.volume) : null,
        unitPrice,
        totalPrice,
        remark: i.remark || null,
        rawQuantity,
        rawUnitPrice,
      }
    })

    const payload = {
      date: returnList.value[0].transactionDate,
      departmentId: returnList.value[0].departmentId,
      companyId: companyId.value,
      branchId: branchId.value,
      created_by: userId.value,
      items: transformedItems
    }
    const res = await apiFetch('/return', { method: 'POST', body: JSON.stringify(payload) })
    if (res.ok) {
      const responseData = await res.json().catch(() => ({}))
      const savedReturnNo = responseData?.returnNo || responseData?.data?.returnNo || ''
      if (savedReturnNo) {
        returnList.value = returnList.value.map((row) => ({ ...row, returnNo: savedReturnNo }))
      }
      alert(savedReturnNo ? `Return saved successfully. Return Number: ${savedReturnNo}` : 'Return saved successfully.')

      setTimeout(async () => {
        const pdfSnapshot = {
          rows: returnList.value.map((row) => ({ ...row })),
          totals: {
            grandTotal: Number(grandTotal.value || 0)
          },
          meta: {
            returnNo: savedReturnNo || returnList.value?.[0]?.returnNo || form.value.returnNo || 'RETURN',
            transactionDate: returnList.value?.[0]?.transactionDate || form.value.transactionDate || new Date().toISOString().slice(0, 10)
          }
        }

        if (confirm("Are you sure..........Print?")) {
          await generateReturnPdf(pdfSnapshot)
        }

        // Clear after possible printing
        returnList.value = []
        localStorage.removeItem('Mina_ret_temp')
        fetchPendingReturns()
        form.value = { ...getInitialForm() }
        // Re-apply department from token
        applyDeptFromToken()
        // Refresh next RT number for the next entry.
        await fetchNextRtNumberForDisplay()
      }, 150)
    } else {
      const err = await res.json()
      alert('Error: ' + err.message)
    }
  } catch (err) { console.error(err) }
  finally { isSaving.value = false }
}

function applyDeptFromToken() {
  const deptList = userDeptList.value
  if (deptList.length === 1) {
    form.value.requestedDepartment = deptList[0]
    form.value.departmentId = resolveDepartmentId(deptList[0])
  }
}

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'PNG'
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'PNG'
}

async function imageUrlToDataUrl(url) {
  if (!url) return { dataUrl: null, format: 'PNG' }
  try {
    const res = await fetch(url)
    if (!res.ok) return { dataUrl: null, format: 'PNG' }
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return {
      dataUrl,
      format: getImageFormatFromDataUrl(dataUrl)
    }
  } catch {
    return { dataUrl: null, format: 'PNG' }
  }
}

async function generateReturnPdf(snapshot = null) {
  const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : returnList.value
  if (!rows.length) return

  const totals = snapshot?.totals || {
    grandTotal: Number(grandTotal.value || 0)
  }
  const meta = snapshot?.meta || {
    returnNo: rows[0]?.returnNo || form.value.returnNo || 'RETURN',
    transactionDate: form.value.transactionDate || new Date().toISOString().slice(0, 10)
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const leftMargin = 16
  const rightMargin = 8
  const topMargin = 8
  const headerBottomY = 28
  const totalPagesExp = '{total_pages_count_string}'
  const today = new Date().toLocaleDateString()
  const { dataUrl: logoDataUrl, format: logoFormat } = await imageUrlToDataUrl(String(printCompanyLogo.value || '').trim())

  const tableBody = rows.map((row, idx) => ([
    String(idx + 1),
    row.returnNo || '-',
    row.requestedDepartment || '-',
    getCategoryName(row.categoryId),
    getItemName(row.itemId),
    String(row.quantity ?? '-'),
    String(row.volume ?? '-'),
    row.expiryDate || '-',
    Number(row.unitPrice || 0).toFixed(2),
    Number(row.totalPrice || 0).toFixed(2),
    row.remark || '-'
  ]))

  autoTable(doc, {
    head: [[
      '#', 'R No', 'Department', 'Category', 'Item', 'Qty',
      'Volume', 'Expiry Date', 'Unit Price', 'Total Cost', 'Reason'
    ]],
    body: tableBody,
    startY: headerBottomY + 2,
    theme: 'grid',
    margin: { left: leftMargin, right: rightMargin, top: headerBottomY + 2, bottom: 12 },
    styles: {
      fontSize: 9.0,
      cellPadding: 1.2,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    },
    bodyStyles: {
      fillColor: [255, 255, 255]
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    didDrawPage: () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, logoFormat, leftMargin, topMargin, 14, 10, undefined, 'FAST')
      }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text(String(printCompanyName.value || 'Company'), pageWidth / 2, topMargin + 3, { align: 'center' })
      doc.setFontSize(11)
      doc.text('Return To Stock (RTS)', pageWidth / 2, topMargin + 8, { align: 'center' })

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(`Date: ${today}`, pageWidth - rightMargin, topMargin + 6, { align: 'right' })

      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.2)
      doc.line(leftMargin, headerBottomY, pageWidth - rightMargin, headerBottomY)

      const pageNo = doc.getNumberOfPages()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.text(`Page ${pageNo} of ${totalPagesExp}`, pageWidth - rightMargin, pageHeight - 5, { align: 'right' })
    }
  })

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  const finalY = (doc.lastAutoTable?.finalY || (headerBottomY + 2)) + 8
  const valueX = pageWidth - rightMargin
  const labelX = valueX - 32

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.line(labelX - 18, finalY + 0.5, valueX, finalY + 0.5)
  doc.text('Total Amount:', labelX, finalY + 5, { align: 'right' })
  doc.text(Number(totals.grandTotal || 0).toFixed(2), valueX, finalY + 5, { align: 'right' })

  const footerY = Math.max(finalY + 22, pageHeight - 28)
  const firstX = leftMargin + 10
  const secondX = pageWidth / 2 - 20
  const thirdX = pageWidth - rightMargin - 56
  const lineW = 40

  doc.setFont('helvetica', 'normal')
  doc.line(firstX, footerY, firstX + lineW, footerY)
  doc.text('Returned By', firstX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(secondX, footerY, secondX + lineW, footerY)
  doc.text('Checked By', secondX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(thirdX, footerY, thirdX + lineW, footerY)
  doc.text('Approved By', thirdX + (lineW / 2), footerY + 5, { align: 'center' })

  const safeReturnNo = String(meta.returnNo || 'RETURN').replace(/[\\/:*?"<>|]/g, '-').trim()
  const safeDate = String(meta.transactionDate || new Date().toISOString().slice(0, 10)).replace(/[\\/:*?"<>|]/g, '-').trim()
  const filename = `${safeReturnNo}-${safeDate}.pdf`
  doc.save(filename)
}

function localSave() { localStorage.setItem('Mina_ret_temp', JSON.stringify(returnList.value)) }
function localLoad() {
  const saved = localStorage.getItem('Mina_ret_temp')
  if (saved) returnList.value = JSON.parse(saved)
}

function getItemName(id) { return registeredItems.value.find(i => i.id === id)?.itemName || '-' }
function getCategoryName(id) { return categories.value.find(c => c.id === id)?.name || '-' }

async function fetchNextRtNumberForDisplay() {
  try {
    if (!companyId.value && !branchId.value) return
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/return/next-number?${query}`)
    if (!res?.ok) return
    const data = await res.json().catch(() => ({}))
    const nextNumber = data?.nextNumber || ''
    // Display-only: do not use this value in save/update payloads.
    rtNumberDisplay.value = nextNumber
  } catch (e) {
    console.error('Error fetching RT number for display:', e)
  }
}

onMounted(async () => {
  await loadData()
  localLoad()
  applyDeptFromToken()
  await fetchNextRtNumberForDisplay()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 12px;
  color: #1e293b;
}

th, td {
  padding: 3px 4px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}



@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 6px !important;
    text-align: left !important;
    color: #000 !important;
    font-size: 8pt !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.delbtn {
  background: none;
  margin-left: 10px;
  border: none;
  color: red;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}
</style>
